import { useEffect, useState } from "react";
import { CheckBox } from "../../components/checkBox/CheckBox";
import { TextField } from "../../components/fields/Fields";
import { SelectFields } from "../../components/select/SelectFields";
import Button from "../../components/button/Button";
import { CheckBoxGroup, CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import { Company } from "../../model/Companies";
import { MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { GroupSelectModal } from "../userList/GroupSelectModal";
import Badge from "../../components/badges/Badges";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { EventParticipationHistorySearchParam } from "../../model/EventParticipationHistory";
import { EventParticipationModal } from "./EventParticipationModal";
import companiesQuery from "../../firestore/Companies.query";

type Props = {
  company: Company | undefined;
  targetUserId: string | undefined;
  targetMessageId: string;
  handleSearch: (condition: any) => void;
};

export const EventParticipationSearch = ({
  company,
  targetUserId,
  targetMessageId,
  handleSearch
}:Props) => {

  // ログイン中の自治体管理者情報
  const dispatch = useDispatch();

  const [showSearchFlg, setShowSearchFlg] = useState(targetUserId === undefined ? true : false);
  const [replyListValue, setReplyListValue] = useState<CheckBoxItem[]>([]);
  const [attendanceListValue, setAttendanceListValue] = useState<CheckBoxItem[]>([]);
  const [publishTarget, setPublishTarget] = useState<string>("");
  const [isOpenGroupSelect, setIsOpenGroupSelect] = useState(false);
  const [defaultGroups, setDefaultGroups] = useState<CheckBoxItem[]>([]);
  const [companyGroups, setCompanyGroups] = useState<CheckBoxItem[]>([]);
  const [searchGroups, setSearchGroups] = useState<CheckBoxItem[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [checkedValue, setCheckedValue] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [eventVenue, setEventVenue] = useState<string>("");
  const [checkedDate, setCheckedDate] = useState<string>("");
  const [sendDateStart, setSendDateStart] = useState<string>("");
  const [sendDateEnd, setSendDateEnd] = useState<string>("");
  const [eventAtStart, setEventAtStart] = useState<string>("");
  const [eventAtEnd, setEventAtEnd] = useState<string>("");
  const [eventType, setEventType] = useState<string>("");
  const [eventTypeList, setEventTypeList] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // アイコン設定
  const searchIcon = {icon: MagnifyingGlassIcon};
  const upIcon = {icon: ChevronUpIcon};
  const downIcon = {icon: ChevronDownIcon};

  // 初期表示
  useEffect(() => {
    (async () => {
      try{
        // 利用者の回答
        let replyListValue = replyList? await Promise.all(replyList.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.title,
            value: false,
          }
        })): [];
        setReplyListValue(replyListValue);

        // 出欠結果
        let attendanceListValue = replyList? await Promise.all(replyList.map((item): CheckBoxItem => {
          return {
            id: "attendance-" + item.id,
            title: item.title,
            value: false,
          }
        })): [];
        setAttendanceListValue(attendanceListValue);

        // イベントタイププルダウン
        let eventTypesList:any = [{id:"", name:""}];
        company?.eventType!.forEach((type:string) => {
          eventTypesList.push({id: type, name:type});
        })
        setEventTypeList(eventTypesList);

        // 配信先グループ選択一覧
        // 有効のグループを取得
        const groupList = (await companiesQuery.getGroups(company?.id!)).filter((group) => group.deleteFlg === false);
        const defaultGroupsValue = groupList.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.name,
            value: false,
            disabled: item.deleteFlg,
          }
        });
        setDefaultGroups(defaultGroupsValue)
        setCompanyGroups(defaultGroupsValue);
        setSearchGroups(defaultGroupsValue);
      }catch(err: any){
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
        console.log(err)
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // 検索画面の表示・非表示の切り替え
  const handleClick = (e:any) => {
    e.preventDefault();
    setShowSearchFlg(!showSearchFlg);
  };

  //チェックボックス（textフォーム）の値を管理
  const changedCheckBox = (value:string) => {
    setLastName("");
    setTitle("");
    setEventVenue("");
    setCheckedValue(checkedValue === value ? "" : value);
  }

  //チェックボックス（日付フォーム）の値を管理
  const changedDateCheckBox = (value:string) => {
    setSendDateStart("");
    setSendDateEnd("");
    setEventAtStart("");
    setEventAtEnd("");
    setCheckedDate(checkedDate === value ? "" : value);
  }

  const replyList = [
    {id:"会場で参加", title:"会場"},
    {id:"オンラインで参加", title:"オンライン"},
    {id:"欠席", title:"欠席"},
    {id:"未回答", title:"未回答"}
  ];

  const publishTargetList = [
    {id:"", name:""},
    {id:company?.displayId, name:"全体"},
    {id:"topic", name:"グループ"},
    {id:"token", name:"個別"}
  ];

  const toggle = () => {
    setSearchGroups(companyGroups);
    setIsOpenGroupSelect(!isOpenGroupSelect);
  };

  const onSelectCancel = async () => {
    // 変更前のグループ選択状態をセット
    setCompanyGroups(searchGroups);
    setIsOpenGroupSelect(!isOpenGroupSelect);
  }

  /** 検索する配信先の変更 */
  const onchangePublishTarget = (select: string) => {
    if(select !== "topic"){
      // グループの選択状態をリセットする
      setCompanyGroups(defaultGroups);
      setSearchGroups(defaultGroups);
    }
  };

  // 検索を実行
  const onClickSearch = async () => {
    let stringCombinationSearchFlg = false;
    if ((
        (targetUserId === undefined ? lastName !== "" : true) ||
        title !== "" ||
        eventVenue !== ""
      ) && (
        replyListValue.filter((reply) => reply.value === true).length > 0 ||
        attendanceListValue.filter((attendance) => attendance.value === true).length > 0 ||
        publishTarget !== "" ||
        searchGroups.filter((group) => group.value === true).length > 0 ||
        (targetUserId === undefined ? userId !== "" : true) ||
        sendDateStart !== "" ||
        sendDateEnd !== "" ||
        eventAtStart !== "" ||
        eventAtEnd !== "" ||
        eventType !== ""
      )
    ) {
      // 文字列前方一致検索と組み合わせた検索の場合
      stringCombinationSearchFlg = true;
    }

    let timestampCombinationSearchFlg = false;
    if ((
        sendDateStart !== "" ||
        sendDateEnd !== "" ||
        eventAtStart !== "" ||
        eventAtEnd !== ""
      ) && (
        replyListValue.filter((reply) => reply.value === true).length > 0 ||
        attendanceListValue.filter((attendance) => attendance.value === true).length > 0 ||
        publishTarget !== "" ||
        searchGroups.filter((group) => group.value === true).length > 0 ||
        (targetUserId === undefined ? userId !== "" : true) ||
        (targetUserId === undefined ? lastName !== "" : true) ||
        title !== "" ||
        eventVenue !== "" ||
        eventType !== ""
      )
    ) {
      // 日付検索と組み合わせた検索の場合
      timestampCombinationSearchFlg = true;
    }

    if (
      replyListValue.filter((reply) => reply.value === true).length === 0 &&
      attendanceListValue.filter((attendance) => attendance.value === true).length === 0 &&
      publishTarget === "" &&
      searchGroups.filter((group) => group.value === true).length === 0 &&
      (targetUserId === undefined ? userId === "" : true) &&
      (targetUserId === undefined ? lastName === "" : true) &&
      title === "" &&
      eventVenue === "" &&
      sendDateStart === "" &&
      sendDateEnd === "" &&
      eventAtStart === "" &&
      eventAtEnd === "" &&
      eventType === ""
    ) {
      // 検索項目がすべて未入力の場合
      setIsDialogOpen(!isDialogOpen);
    } else {
      // 検索項目の入力状態を取得する
      const searchParams: EventParticipationHistorySearchParam = {
        messageId: targetMessageId,
        replyListValue: replyListValue.filter((attendance) => attendance.value === true),
        attendanceListValue: attendanceListValue.filter((attendance) => attendance.value === true),
        publishTarget: publishTarget,
        searchGroups: searchGroups.filter((group) => group.value === true),
        userId: targetUserId ?? userId,
        lastName: lastName,
        title: title,
        eventVenue: eventVenue,
        sendDateStart: sendDateStart,
        sendDateEnd: sendDateEnd,
        eventAtStart: eventAtStart,
        eventAtEnd: eventAtEnd,
        eventType: eventType,
        stringCombinationSearchFlg: stringCombinationSearchFlg,
        timestampCombinationSearchFlg: timestampCombinationSearchFlg
      }

      handleSearch(searchParams);
    }
  };

  return(
    <div className="flex flex-col">
      <div className="inline-block min-w-full p-1 align-middle">
        <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full table-fixed divide-y">
            <div className="items-center p-2">
              <div className="flex justify-between cursor-pointer" onClick={(e) => handleClick(e)}>
                <h2 className=" flex font-semibold text-gray-900 items-center">
                  <searchIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                  />
                  検索
                </h2>
                {showSearchFlg?(
                  <>
                    <upIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                    />
                  </>
                ):(
                  <>
                    <downIcon.icon
                    className={"flex-shrink-0 h-5 w-5"}
                    aria-hidden="true"
                    />
                  </>
                )}
              </div>
              {showSearchFlg?(
                <>
                  <h3 className="text-sm text-gray-500 my-1">&nbsp;複数項目を入力した場合はAND検索となります</h3>
                  <div className="flex gap-6 px-2">
                    <div className="w-4/12 min-w-[385px] flex flex-col gap-1">
                      <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg min-w-min max-w-md">
                        <div className="p-2 flex flex-col gap-1">
                          <h3 className="text-sm text-gray-500 mb-1 ml-2">前方一致検索 ※いずれか１つの項目を選択してください</h3>
                          {targetMessageId === "" ?
                            <div className="flex items-center">
                              <div className="w-3/12">
                                <h3 className="text-sm text-gray-900">タイトル</h3>
                              </div>
                              <div className="w-1/12">
                                <CheckBox
                                  id="title"
                                  value={checkedValue === "title"}
                                  onChange={() => {changedCheckBox("title")}}
                                />
                              </div>
                              <div className="w-8/12 flex items-center">
                                <TextField
                                  id=""
                                  className={'h-10 w-44'}
                                  inputClassName={`${checkedValue === "title" ? "" : "bg-gray-300"} text-sm h-8`}
                                  value={title}
                                  disabled={checkedValue !== "title"}
                                  onChange={(e:any) => {setTitle(e.target.value)}}
                                />
                              </div>
                            </div>
                          :
                            <></>
                          }
                          <div className="flex items-center">
                            <div className="w-3/12">
                              <h3 className="text-sm text-gray-900">会場</h3>
                            </div>
                            <div className="w-1/12">
                              <CheckBox
                                id="eventVenue"
                                value={checkedValue === "eventVenue"}
                                onChange={() => {changedCheckBox("eventVenue")}}
                              />
                            </div>
                            <div className="w-8/12 flex items-center">
                              <TextField
                                id=""
                                className={'h-10 w-44'}
                                inputClassName={`${checkedValue === "eventVenue" ? "" : "bg-gray-300"} text-sm h-8`}
                                value={eventVenue}
                                disabled={checkedValue !== "eventVenue"}
                                onChange={(e:any) => {setEventVenue(e.target.value)}}
                              />
                            </div>
                          </div>
                          {targetUserId === undefined ?
                            <div className="flex items-center">
                              <div className="w-3/12">
                                <h3 className={`text-sm ${targetUserId === undefined ? "text-gray-900" : "text-gray-400"}`}>名前</h3>
                              </div>
                              <div className="w-1/12">
                                <CheckBox
                                  id="lastName"
                                  value={checkedValue === "lastName"}
                                  onChange={() => {changedCheckBox("lastName")}}
                                />
                              </div>
                              <div className="w-8/12 flex items-center">
                                <TextField
                                  id=""
                                  className={'h-10 w-44'}
                                  inputClassName={`${checkedValue === "lastName" ? "" : "bg-gray-300"} text-sm h-8`}
                                  value={lastName}
                                  disabled={checkedValue !== "lastName"}
                                  onChange={(e:any) => {setLastName(e.target.value)}}
                                />
                              </div>
                            </div>
                          :
                            <></>
                          }
                        </div>
                      </div>
                      <div className="px-2 min-w-min max-w-md">
                        <div className="flex items-center my-1 flex-wrap">
                          <div className="w-4/12">
                            <h3 className="text-sm text-gray-900">配信先</h3>
                          </div>
                          <div className="w-8/12">
                            <SelectFields
                              id="publishTarget"
                              color="indigo"
                              value={publishTarget}
                              className={"h-10 w-44"}
                              selectItems={publishTargetList}
                              onChange={(e:any) => {
                                setPublishTarget(e.target.value);
                                onchangePublishTarget(e.target.value);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {publishTarget === "topic"? 
                      <>
                        <div className="flex max-w-md">
                          <div className="w-9/12 text-left items-start ml-2">
                            <div className="h-12 mr-2 border-2 border-solid overflow-y-scroll">
                              {searchGroups.filter((group) => group.value === true).map(
                                (item: any) => {
                                  return (
                                    <Badge color="blue">
                                      {item.title}
                                    </Badge>
                                  )
                                }
                              )}
                            </div>
                          </div>
                          <div className="w-3/12 flex justify-end">
                            <Button
                              className={"h-12 w-20 !px-2 2xl:!px-4 text-sm items-center rounded-full"}
                              onClick={toggle}
                              children={"グループ選択"}
                            />
                          </div>
                        </div>
                        <h3 className="text-xs text-gray-500">複数選択した場合いずれかに配信するお知らせすべてが検索されます</h3>
                        <GroupSelectModal
                          open={isOpenGroupSelect}
                          toggle={toggle}
                          closeOnClickOutside
                          companyGroupsValue={companyGroups}
                          setState={setCompanyGroups}
                          children={undefined}
                          dispButtonFlg={true}
                          onSelectCancel={onSelectCancel}
                        />
                      </>
                      :
                      <></>
                      }
                    </div>
                    <div className="w-4/12 flex flex-col gap-1">
                      <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg min-w-min max-w-md">
                        <div className="p-2 flex flex-col gap-1">
                          <h3 className="text-sm text-gray-500 mb-1 ml-2">いずれか１つの項目を選択してください</h3>
                          <div className="flex items-center flex-wrap">
                            <div className="w-3/12">
                              <h3 className="text-sm text-gray-900">配信日</h3>
                            </div>
                            <div className="flex items-center">
                              <CheckBox
                                id="sendDate"
                                value={checkedDate === "sendDate"}
                                onChange={() => {changedDateCheckBox("sendDate")}}
                              />
                              <TextField
                                value={sendDateStart}
                                className={"h-10 mr-1 w-[7.5rem] xl:w-32"}
                                inputClassName={`${checkedDate === "sendDate" ? "" : "bg-gray-300"} text-sm h-8 !px-2 xl:!px-3`}
                                name={"sendDate_s"}
                                onChange={(e:any) => {setSendDateStart(e.target.value)}}
                                type={"date"}
                                disabled={checkedDate !== "sendDate"}
                              />
                              ～
                              <TextField
                                value={sendDateEnd}
                                className={"h-10 ml-1 w-[7.5rem] xl:w-32"}
                                inputClassName={`${checkedDate === "sendDate" ? "" : "bg-gray-300"} text-sm h-8 !px-2 xl:!px-3`}
                                name={"sendDate_e"}
                                onChange={(e:any) => {setSendDateEnd(e.target.value)}}
                                type={"date"}
                                disabled={checkedDate !== "sendDate"}
                              />
                            </div>
                          </div>
                          <div className="flex items-center flex-wrap">
                            <div className="w-3/12">
                              <h3 className="text-sm text-gray-900 whitespace-pre">イベント開催日</h3>
                            </div>
                            <div className="flex items-center">
                              <CheckBox
                                id="eventAt"
                                value={checkedDate === "eventAt"}
                                onChange={() => {changedDateCheckBox("eventAt")}}
                              />
                              <TextField
                                value={eventAtStart}
                                className={"h-10 mr-1 w-[7.5rem] xl:w-32"}
                                inputClassName={`${checkedDate === "eventAt" ? "" : "bg-gray-300"} text-sm h-8 !px-2 xl:!px-3`}
                                name={"eventAt_s"}
                                onChange={(e:any) => {setEventAtStart(e.target.value)}}
                                type={"date"}
                                disabled={checkedDate !== "eventAt"}
                              />
                              ～
                              <TextField
                                value={eventAtEnd}
                                className={"h-10 ml-1 w-[7.5rem] xl:w-32"}
                                inputClassName={`${checkedDate === "eventAt" ? "" : "bg-gray-300"} text-sm h-8 !px-2 xl:!px-3`}
                                name={"eventAt_e"}
                                onChange={(e:any) => {setEventAtEnd(e.target.value)}}
                                type={"date"}
                                disabled={checkedDate !== "eventAt"}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="px-2 min-w-min max-w-md">
                        <div className="flex items-center mt-1">
                          <div className="w-5/12 xl:w-4/12">
                            <h3 className="text-sm text-gray-900">イベントタイプ</h3>
                          </div>
                          <div className="flex items-center">
                            <SelectFields
                              id="eventType"
                              color="indigo"
                              value={eventType}
                              className={"h-10 w-44"}
                              selectItems={eventTypeList}
                              onChange={(e:any) => {setEventType(e.target.value)}}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="w-4/12 min-w-[360px] flex flex-col gap-1">
                      {targetUserId === undefined ?
                        <div className="flex items-center mb-1 flex-wrap">
                          <div className="w-3/12">
                            <h3 className="text-sm text-gray-900">利用者番号</h3>
                          </div>
                          <div className="ml-2">
                            <TextField
                              id="userId"
                              className={'h-10 w-44'}
                              inputClassName={`text-sm h-8`}
                              value={userId}
                              onChange={(e:any) => {setUserId(e.target.value)}}
                              maxLength={11}
                            />
                          </div>
                        </div>
                      :
                        <></>
                      }
                      <div className="flex items-center my-1 flex-wrap">
                        <div className="w-3/12">
                          <h3 className="text-sm text-gray-900 whitespace-pre">利用者の回答</h3>
                        </div>
                        <div className="ml-2">
                          <CheckBoxGroup
                            children={undefined}
                            setState={setReplyListValue}
                            name={"group"}
                            checkBoxItem={replyListValue}
                            vertical={true}
                            className={"flex-nowrap"}
                          />
                        </div>
                      </div>
                      <div className="flex items-center my-1 flex-wrap">
                        <div className="w-3/12">
                          <h3 className="text-sm text-gray-900">出欠結果</h3>
                        </div>
                        <div className="ml-2">
                          <CheckBoxGroup
                            children={undefined}
                            setState={setAttendanceListValue}
                            name={"group"}
                            checkBoxItem={attendanceListValue}
                            vertical={true}
                            className={"flex-nowrap"}
                          />
                        </div>
                      </div>
                      <div className={`flex flex-grow items-end justify-end mt-2 mb-2`}>
                        <Button
                          className={"h-10 w-32 text-sm items-center"}
                          onClick={() => onClickSearch()}
                          children={"検索"}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ):(
                <>
                </>
              )}
              
            </div>
          </table>
        </div>
      </div>
      <EventParticipationModal
        children={undefined}
        open={isDialogOpen}
        toggle={() => {setIsDialogOpen(!isDialogOpen)}}
        closeOnClickOutside={false}
      />
    </div>
  )
};